import { Autocomplete, TextField, TextFieldVariants } from "@mui/material";
import { ANY_PROFESSION_ID } from "constants/skuQualificationConstants";
import { ALL_APPROVED_ID } from "helpers/skuQualificationHelpers";
import useGlobalContext from "hooks/globalData";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  BaseFilter,
  ListRequestOfBaseFilter,
  Profession,
} from "services/KnowfullyAdminClient";
import { ProfessionIds } from "types/common-types";

type SelectProfessionMultipleProps = {
  value: string[];
  onChange: (v: string[]) => void;
  variant?: TextFieldVariants;
  disabled?: boolean;
  // Shows the selection without offering to change it: no chip remove, no popup.
  readOnly?: boolean;
  // Offers the two authoring sentinels the SKU qualification save resolves through
  // processFormValues. Filters only pick real professions, so they stay opt-in.
  sentinelsAllowed?: boolean;
};

const SelectProfessionMultiple = ({
  onChange,
  value,
  variant,
  disabled,
  readOnly = false,
  sentinelsAllowed = false,
}: SelectProfessionMultipleProps) => {
  const { skuService } = useGlobalContext();
  const { formatMessage: t } = useIntl();

  const request = ListRequestOfBaseFilter.fromJS({
    sortField: "name",
    parameters: BaseFilter.fromJS({
      excludedIds: sentinelsAllowed ? [] : [ProfessionIds.AnyProfession],
    }),
  })

  const { data, isFetching } = useQuery(
    ["skuService.getProfessions", request],
    () =>
      skuService.getProfessions(request)
  );

  const handleChangeMultiple = (_: any, values: Profession[]) => {
    onChange(values.map((v) => v.id!));
  };

  // Same order and labels as SelectProfessionExtended, so a row authored in either picker
  // reads the same.
  const sentinels: Profession[] = sentinelsAllowed
    ? [
        {
          id: ALL_APPROVED_ID,
          name: t({ id: "allApprovedProfessionsLabel" }),
        } as Profession,
        { id: ANY_PROFESSION_ID, name: t({ id: "anyLabel" }) } as Profession,
      ]
    : [];

  const professions = sentinels.concat(data?.data ?? []);

  return (
    <Autocomplete
      multiple
      options={professions}
      getOptionLabel={(option) => option.name ?? "?"}
      value={professions.filter((r) => value.includes(r.id!) ?? false)}
      onChange={handleChangeMultiple}
      loading={isFetching}
      disabled={disabled}
      readOnly={readOnly}
      forcePopupIcon={readOnly ? false : "auto"}
      ChipProps={{ size: "small" }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          label={t({ id: "skuProfessionLabel" })}
        />
      )}
    />
  );
};

export default SelectProfessionMultiple;
